// ============================================================================
// CUSTOM BUTTON INJECTION MODULE
// ============================================================================

import waitUntil from '@/utils/waitUntil';

const CHAINLIT_CONTAINER = 'chainlit-copilot';
const SHADOW_ROOT = 'cl-shadow-root';
const CUSTOM_BUTTON_SELECTOR = '[data-sifflet-custom-close]';
const CUSTOM_BUTTON_ATTR = 'data-sifflet-custom-close';

/**
 * Get the active Chainlit widget host. When there are duplicates (e.g. after unmount/remount),
 * returns the last one in the document (the most recently mounted).
 */
const getChainlitContainer = (): HTMLElement | null => {
  const all = document.querySelectorAll(`#${CHAINLIT_CONTAINER}`);
  return all.length ? (all[all.length - 1] as HTMLElement) : null;
};

/**
 * Get the Chainlit widget shadow root element from the given host (or the active host).
 */
const getShadowRoot = (host?: HTMLElement | null): HTMLElement | null => {
  const el = host ?? getChainlitContainer();
  return el?.shadowRoot?.getElementById(SHADOW_ROOT) ?? null;
};

/**
 * Find the header button container by looking for the button with id "new-chat-button".
 * The container is the parent of the div that contains the new-chat-button.
 * The custom close button will be appended as the last child (third place).
 */
const findButtonContainer = (root: HTMLElement): HTMLElement | null => {
  // Find the new-chat-button
  const newChatButton = root.querySelector('button#new-chat-button') as HTMLButtonElement | null;
  if (!newChatButton) return null;

  // Find the container: the new-chat-button is inside a div, so we need to go up two levels
  const newChatButtonParent = newChatButton.parentElement;
  if (!newChatButtonParent) return null;

  const container = newChatButtonParent.parentElement;
  if (!container || container.id === SHADOW_ROOT || container === root) return null;

  return container;
};

/**
 * Create the custom close button element
 */
const createCloseButton = (onClose: () => void): HTMLButtonElement => {
  const button = document.createElement('button');
  button.setAttribute(CUSTOM_BUTTON_ATTR, 'true');
  button.setAttribute('aria-label', 'Close chat');
  button.setAttribute('type', 'button');
  button.className = 'sifflet-custom-close-button';

  const icon = document.createElement('span');
  icon.textContent = '×';
  icon.setAttribute('aria-hidden', 'true');
  button.appendChild(icon);

  button.addEventListener('click', (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
  });

  return button;
};

/**
 * Wait for the button container to appear
 * Times out after 5 seconds if the container never appears
 */
async function waitForButtonContainer(
  shadowRoot: ShadowRoot,
): Promise<HTMLElement | null> {
  const shadowRootElement = shadowRoot.getElementById(SHADOW_ROOT);
  if (!shadowRootElement) return null;

  let container: HTMLElement | null = findButtonContainer(shadowRootElement);
  if (container) return container;

  await waitUntil(() => {
    container = findButtonContainer(shadowRootElement);
    return !!container;
  }, 100, 5000);

  return container;
}

/**
 * Inject a custom close button into the Chainlit widget header.
 * Waits for the target container to exist if needed, then injects exactly once.
 */
export default async function injectCustomCloseButton(onClose: () => void): Promise<void> {
  const chainlitContainer = getChainlitContainer();
  if (!chainlitContainer?.shadowRoot) return;

  const shadowRoot = getShadowRoot(chainlitContainer);
  if (!shadowRoot) return;

  // Check if already injected
  if (shadowRoot.querySelector(CUSTOM_BUTTON_SELECTOR)) return;

  // Find or wait for the container
  const container = await waitForButtonContainer(chainlitContainer.shadowRoot);
  if (!container) return;

  container.appendChild(createCloseButton(onClose));
}
